import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { useI18n } from "../i18n";
import { useCart } from "../cart";
import { eur } from "../lib/format";
import * as apiClient from "../api";

export default function Checkout() {
  const { t, lang } = useI18n();
  const { items, total } = useCart();
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", country: "IT", marketing_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const referral = localStorage.getItem("cristalda_ref") || null;
  const { data: settings } = useQuery({ queryKey: ["publicSettings"], queryFn: apiClient.getPublicSettings });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error(t("cart.empty")); return; }
    setSubmitting(true);
    try {
      const res = await apiClient.createCheckout({
        items: items.map((i) => ({ experience_id: i.experience_id, date: i.date, time: i.time, participants: i.participants })),
        customer: { ...form, language: lang },
        referral_code: referral,
        origin_url: window.location.origin,
      });
      localStorage.setItem("cristalda_pending_booking", res.booking_number);
      localStorage.setItem("cristalda_pending_token", res.access_token || "");
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("checkout.bookingerror"));
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-8">{t("checkout.title")}</h1>
        <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl border border-slate-100 p-6 bg-white shadow-sm">
              <h3 className="font-heading font-semibold text-slate-900 mb-4">{t("checkout.details")}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {[["name", t("checkout.name")], ["surname", t("checkout.surname")], ["email", t("checkout.email")], ["phone", t("checkout.phone")]].map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
                    <input required={k !== "phone"} type={k === "email" ? "email" : "text"} data-testid={`checkout-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-secondary" />
                  </label>
                ))}
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{t("checkout.country")}</span>
                  <select value={form.country} data-testid="checkout-country" onChange={(e) => set("country", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-secondary">
                    {["IT", "DE", "GB", "FR", "US", "ES", "NL"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <label className="flex items-center gap-2 mt-4 text-sm text-slate-600">
                <input type="checkbox" data-testid="checkout-consent" checked={form.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} className="h-4 w-4 rounded" />
                {t("checkout.consent")}
              </label>
            </div>

            <div className="rounded-3xl border border-slate-100 p-6 bg-white shadow-sm">
              <h3 className="font-heading font-semibold text-slate-900 mb-4">{t("checkout.payment")}</h3>
              <div className="rounded-2xl border-2 border-primary bg-primary/5 p-4 flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-semibold text-slate-800">{t("checkout.mock")}</div>
                  <div className="text-xs text-slate-500">{t("checkout.norealpayment")}</div>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-400 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {t("checkout.gdpr")}</p>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 rounded-3xl border border-slate-100 shadow-lg p-6 bg-white">
              <h3 className="font-heading font-semibold text-slate-900 mb-4">{t("checkout.yourholiday")}</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto no-scrollbar mb-4">
                {items.map((i) => (
                  <div key={i._key} className="flex gap-3">
                    <img src={i.image} alt="" className="h-12 w-14 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 line-clamp-1">{i.title}</div>
                      <div className="text-xs text-slate-400">{i.date} · {i.time} · {i.participants}p</div>
                    </div>
                    <span className="text-sm font-bold text-primary whitespace-nowrap">{eur(i.subtotal)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between py-3 border-t border-slate-100">
                <span className="font-semibold text-slate-800">{t("cart.total")}</span>
                <span className="text-2xl font-heading font-bold text-primary">{eur(total)}</span>
              </div>
              {settings?.prelaunch_enabled ? (
                <div data-testid="prelaunch-notice" className="w-full mt-4 rounded-2xl bg-secondary/10 border border-secondary/30 text-center py-4 px-3 text-primary font-semibold">
                  {lang === "it" && settings.prelaunch_text ? settings.prelaunch_text : t("exp.prelaunchdefault")}
                </div>
              ) : (
                <button type="submit" disabled={submitting} data-testid="confirm-booking-btn" className="w-full mt-4 rounded-full bg-primary text-white font-semibold py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-60">
                  {submitting ? t("common.loading") : t("checkout.pay")}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}
